/**
 * CropRecommendationPage.tsx
 * 
 * Page component for the crop recommendation feature
 */

import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Divider,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CropRecommendationForm from '../components/crop-recommendation/CropRecommendationForm';
import CropRecommendationResults from '../components/crop-recommendation/CropRecommendationResults';
import { useAppSelector } from '../hooks/reduxHooks';
import { selectWaterQualityData } from '../store/slices/waterQualitySlice';
import { selectCropRecommendationData } from '../store/slices/cropRecommendationSlice';
import { WaterQualityParameters } from '../types/waterQuality';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`crop-rec-tabpanel-${index}`}
      aria-labelledby={`crop-rec-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `crop-rec-tab-${index}`,
    'aria-controls': `crop-rec-tabpanel-${index}`,
  };
}

const CropRecommendationPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();
  const waterQuality = useAppSelector(selectWaterQualityData);
  const recommendations = useAppSelector(selectCropRecommendationData);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleRecommendationGenerated = () => {
    setTabValue(1); // Switch to results tab when recommendations are generated
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Crop Nutrient Recommendations
        </Typography>
      </Box>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Generate crop-specific nutrient recommendations for hydroponics based on crop types, growth stages, water quality, and environmental conditions.
      </Typography>
      
      <Paper sx={{ mt: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="crop recommendation tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Generate Recommendations" {...a11yProps(0)} />
          <Tab 
            label="View Results" 
            {...a11yProps(1)} 
            disabled={!recommendations}
          />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>          <CropRecommendationForm 
            waterQuality={waterQuality as WaterQualityParameters | undefined}
            onRecommendationGenerated={handleRecommendationGenerated}
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          {recommendations && (
            <CropRecommendationResults recommendations={recommendations} />
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default CropRecommendationPage;
